import React from 'react';
import { Sparkles, Gauge, Target, Check, FileDown } from 'lucide-react';
import { ResumeThumb } from './ResumeThumb';
import { ExampleBadge } from './Section';

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-medium text-muted-foreground">{label}</div>
    <div className="mt-1 rounded-md border border-border bg-white px-2 py-1.5 text-[11px] text-primary">{value}</div>
  </div>
);

export const BrowserFrame = ({ children, className = '' }) => (
  <div className={`overflow-hidden rounded-2xl border border-border bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_24px_48px_-24px_rgba(16,24,40,0.25)] ${className}`}>
    <div className="flex items-center gap-1.5 border-b border-border bg-surface px-4 py-2.5" aria-hidden="true">
      <span className="h-2.5 w-2.5 rounded-full bg-border" /><span className="h-2.5 w-2.5 rounded-full bg-border" /><span className="h-2.5 w-2.5 rounded-full bg-border" />
      <span className="ms-3 h-5 flex-1 max-w-xs rounded-md bg-white text-center text-[10px] leading-5 text-muted-foreground">careerova.tech/builder</span>
    </div>
    {children}
  </div>
);

// Hero product showcase: editor + live preview + AI/ATS panel, built from real UI pieces.
export const ProductMockup = () => (
  <div className="relative" data-testid="product-mockup">
    <BrowserFrame>
      <div className="grid grid-cols-1 gap-0 sm:grid-cols-[160px_minmax(0,1fr)] lg:grid-cols-[180px_minmax(0,1fr)_200px]">
        <aside className="hidden border-e border-border bg-surface/60 p-3 sm:block" aria-hidden="true">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Experience</div>
          <div className="mt-2 space-y-2">
            <Field label="Role" value="Senior Product Designer" />
            <Field label="Company" value="Fintech Co." />
            <div>
              <div className="text-[10px] font-medium text-muted-foreground">Bullet points</div>
              <div className="mt-1 rounded-md border border-border bg-white px-2 py-1.5 text-[10px] leading-snug text-primary">Led a redesign that lifted activation by 32%</div>
            </div>
            <div className="mt-1 inline-flex w-full items-center justify-center gap-1 rounded-md bg-accent/10 px-2 py-1.5 text-[10px] font-semibold text-accent"><Sparkles className="h-3 w-3" /> Improve with AI</div>
          </div>
          <div className="mt-3 rounded-lg border border-accent/30 bg-accent/5 p-2">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-accent">AI suggestion</div>
            <p className="mt-1 text-[10px] leading-snug text-primary">Built the design system adopted by 5 squads, cutting UI delivery time by 40%.</p>
            <div className="mt-1.5 flex gap-1"><span className="rounded bg-accent px-1.5 py-0.5 text-[9px] font-semibold text-white">Use</span><span className="rounded border border-border px-1.5 py-0.5 text-[9px] text-muted-foreground">Edit</span></div>
          </div>
        </aside>

        <div className="bg-[#EEF1F5] p-3 sm:p-4">
          <ResumeThumb templateId="modern" accent="#0A0F1C" className="rounded-md shadow-md ring-1 ring-black/5" maxHeight={340} />
        </div>

        <aside className="min-w-0 border-t border-border bg-white p-3 sm:col-span-2 lg:col-span-1 lg:border-s lg:border-t-0" aria-hidden="true">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><Target className="h-3 w-3" /> Target job</div>
            <ExampleBadge />
          </div>
          <div className="mt-1.5 rounded-md border border-border bg-surface px-2 py-1.5 text-[11px] font-medium text-primary">Product Designer · Fintech</div>
          <div className="mt-3 flex items-center gap-2">
            <div className="relative flex h-14 w-14 items-center justify-center">
              <svg className="h-14 w-14 -rotate-90" viewBox="0 0 100 100"><circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="9" /><circle cx="50" cy="50" r="42" fill="none" stroke="#059669" strokeWidth="9" strokeLinecap="round" strokeDasharray="222 264" /></svg>
              <div className="absolute text-center"><div className="text-sm font-bold text-primary">84</div></div>
            </div>
            <div><div className="flex items-center gap-1 text-[11px] font-semibold text-primary"><Gauge className="h-3 w-3 text-accent" /> Resume match</div><div className="text-[10px] text-muted-foreground">Keyword alignment: Strong</div></div>
          </div>
          <div className="mt-3">
            <div className="text-[10px] font-semibold text-primary">Matched keywords</div>
            <div className="mt-1 flex flex-wrap gap-1">{['Figma', 'Design systems', 'User research'].map((k) => <span key={k} className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700"><Check className="h-2.5 w-2.5" />{k}</span>)}</div>
          </div>
          <div className="mt-2">
            <div className="text-[10px] font-semibold text-primary">Missing keywords</div>
            <div className="mt-1 flex flex-wrap gap-1">{['A/B testing', 'Fintech compliance'].map((k) => <span key={k} className="rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">{k}</span>)}</div>
          </div>
          <div className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md bg-primary px-2 py-1.5 text-[10px] font-semibold text-white"><FileDown className="h-3 w-3" /> Export PDF</div>
        </aside>
      </div>
    </BrowserFrame>
  </div>
);
